import React, { useEffect, useState } from 'react';
import { X, Clock, AlertTriangle, ChevronRight, User, Calendar, ExternalLink, History, Activity } from 'lucide-react';
import api from './axiosConfig';
import { useNavigate } from 'react-router-dom';

const IncidentHistoryModal = ({ isOpen, onClose, assetnum, locationId, title }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      const fetchHistory = async () => {
        setLoading(true);
        try {
            console.log("Fetching history with params:", { assetnum, locationId });
          const params = assetnum ? { assetnum: assetnum } : { location_id: locationId };
          const res = await api.get('/incidents/history', { params });
          setHistory(res.data);
        } catch (err) {
          console.error("Failed to load history");
        } finally {
          setLoading(false);
        }
      };
      fetchHistory();
    }
  }, [isOpen, assetnum, locationId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-[#f3f4f6] w-[700px] h-[85vh] rounded-lg shadow-2xl overflow-hidden animate-in zoom-in duration-200 flex flex-col border border-white/20">
        
        {/* Header - نفس روح نظام تكنويا */}
        <div className="bg-[#1a3353] text-white p-4 flex justify-between items-center shadow-lg">
          <div className="flex items-center gap-3">
            <div className="bg-orange-500 p-1.5 rounded-sm">
              <History size={18} className="text-white" />
            </div>
            <div>
              <h2 className="font-black uppercase tracking-tighter text-sm">Historical Incidents</h2>
              <p className="text-[9px] text-blue-300 font-bold uppercase tracking-widest">{title}</p>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-white/10 p-1 rounded-full transition-all"><X size={22}/></button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-5 custom-scrollbar">
          {loading ? (
            <div className="h-full flex flex-col items-center justify-center space-y-3 opacity-50">
              <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
              <p className="font-black text-[10px] uppercase tracking-[0.3em] text-[#005495]">Retrieving Logs...</p>
            </div>
          ) : history.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center opacity-20">
              <Clock size={64} className="mb-2" />
              <p className="font-black uppercase tracking-[0.2em] text-lg">No Records Found</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-white p-2 px-4 rounded border-b border-gray-200 flex justify-between items-center shadow-sm">
                 <span className="text-[10px] font-black text-gray-400 uppercase">Found {history.length} Incident(s)</span>
              </div>

              <div className="grid gap-3">
                {history.map((inc) => (
                  <div 
                    key={inc.id}
                    onClick={() => {
                        onClose();
                        navigate(`/incidents/${inc.id}`);
                    }}
                    className="bg-white border border-gray-200 rounded-sm p-4 hover:shadow-md hover:border-blue-300 transition-all cursor-pointer group relative overflow-hidden"
                  >
                    {/* Status Ribbon */}
                    <div className={`absolute top-0 right-0 h-full w-1 ${inc.status === 'NEW' ? 'bg-orange-500' : 'bg-blue-600'}`} />
                    
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-black text-[#005495] text-[11px] uppercase tracking-tighter bg-blue-50 px-2 py-0.5 rounded">{inc.ticket_id}</span>
                        <span className={`text-[8px] font-black px-1.5 py-0.5 rounded text-white ${inc.priority === 1 ? 'bg-red-600' : 'bg-gray-400'}`}>
                           P{inc.priority}
                        </span>
                      </div>
                      <span className="text-[9px] font-mono text-gray-400 font-bold flex items-center gap-1 bg-gray-50 px-2 py-0.5 rounded">
                        <Calendar size={10}/> {new Date(inc.report_date).toLocaleDateString()}
                      </span>
                    </div>

                    <h4 className="font-black text-gray-700 text-[12px] mb-1 group-hover:text-blue-700 transition-colors uppercase">{inc.description}</h4>
                    <p className="text-gray-500 text-[10px] line-clamp-2 italic mb-3 font-medium">
                        {inc.long_description || 'No detailed log provided for this entry.'}
                    </p>

                    <div className="flex items-center justify-between border-t pt-3 mt-1 border-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5 text-[9px] text-gray-400 font-bold uppercase">
                          <User size={12} className="text-blue-400"/> {inc.reported_by}
                        </div>
                        <div className={`text-[9px] font-black uppercase flex items-center gap-1 ${inc.status === 'NEW' ? 'text-orange-600' : 'text-blue-600'}`}>
                           <Activity size={10}/> {inc.status}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-blue-500 font-black text-[9px] uppercase opacity-0 group-hover:opacity-100 transition-all">
                        View Ticket <ExternalLink size={10} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white p-3 border-t flex justify-end">
           <button onClick={onClose} className="px-6 py-1.5 bg-gray-800 text-white font-black rounded-sm text-[10px] uppercase hover:bg-black transition-all">Close</button>
        </div>
      </div>
    </div>
  );
};

export default IncidentHistoryModal;